import { useSearchParams } from 'react-router';
import { Group, Select } from '@mantine/core';
import { YearPickerInput } from '@mantine/dates';
import { ReportInterval } from '../../types/reports';
import { periodOptions, selectedYear, selectedPeriod, searchParamsFor } from './periodFilterModel';

/**
 * D-11: eine intervallgesteuerte Filterkomponente ersetzt die drei fruehere, je auf ein Intervall
 * zugeschnittene Filterkomponenten (Monat/Quartal/Jahr) -- die drei unterschieden sich bisher in
 * Props, Vorbelegung, URL-Parameter und Eingabeart, ohne dass das je entschieden wurde. Die
 * monatliche Vorgaengerkomponente hatte schon vor dieser Ablösung keinen Aufrufer (toter Code) --
 * der monatliche Zweig entsteht hier trotzdem, weil Formular A/B als Meldung monatlich ist und mit
 * `inlandsversand` ein monatlicher Auswertungstyp existiert.
 *
 * Auswahl, Vorbelegung und Parameterbildung laufen ausschliesslich ueber `periodFilterModel.ts` --
 * hier steht keine zweite Ableitung von Jahr oder Periode.
 */
interface PeriodFilterProps {
  interval: ReportInterval;
  availableYears: number[];
  loading?: boolean;
}

export function PeriodFilter({ interval, availableYears, loading = false }: PeriodFilterProps) {
  const [searchParams, setSearchParams] = useSearchParams();

  const params = Object.fromEntries(searchParams.entries());
  const year = selectedYear(params, availableYears);
  const period = selectedPeriod(params, interval);

  const minDate = availableYears.length > 0 ? `${Math.min(...availableYears)}-01-01` : undefined;
  const maxDate = availableYears.length > 0 ? `${Math.max(...availableYears)}-12-31` : undefined;

  const handleYearPickerChange = (dateStr: string | null) => {
    if (dateStr) {
      const newYear = new Date(dateStr).getFullYear();
      setSearchParams(searchParamsFor(newYear, period, interval), { replace: true });
    }
  };

  const handleYearSelectChange = (value: string | null) => {
    if (value) {
      setSearchParams(searchParamsFor(Number(value), period, interval), { replace: true });
    }
  };

  const handlePeriodChange = (value: string | null) => {
    if (value) {
      setSearchParams(searchParamsFor(year, value, interval), { replace: true });
    }
  };

  const yearSelectOptions = availableYears.map((y) => ({ value: String(y), label: String(y) }));

  switch (interval) {
    case ReportInterval.YEARLY:
      return (
        <YearPickerInput
          label="Jahr"
          value={`${year}-01-01`}
          onChange={handleYearPickerChange}
          w={120}
          minDate={minDate}
          maxDate={maxDate}
          disabled={loading || availableYears.length === 0}
          allowDeselect={false}
          getYearControlProps={(dateStr) => {
            const y = new Date(dateStr).getFullYear();
            if (!availableYears.includes(y)) {
              return { disabled: true };
            }
            return {};
          }}
        />
      );

    case ReportInterval.QUARTERLY:
      return (
        <Group gap="sm">
          <Select
            label="Quartal"
            w={150}
            value={period}
            onChange={handlePeriodChange}
            data={periodOptions(interval)}
            allowDeselect={false}
            disabled={loading}
          />
          <Select
            label="Jahr"
            w={100}
            value={String(year)}
            onChange={handleYearSelectChange}
            data={yearSelectOptions}
            allowDeselect={false}
            disabled={loading || availableYears.length === 0}
          />
        </Group>
      );

    case ReportInterval.MONTHLY:
      return (
        <Group gap="sm">
          <Select
            label="Monat"
            w={200}
            value={period}
            onChange={handlePeriodChange}
            data={periodOptions(interval)}
            allowDeselect={false}
            disabled={loading}
          />
          <Select
            label="Jahr"
            w={100}
            value={String(year)}
            onChange={handleYearSelectChange}
            data={yearSelectOptions}
            allowDeselect={false}
            disabled={loading || availableYears.length === 0}
          />
        </Group>
      );
  }
}
